import { DEFAULT_ISLAND_KEY, getIslandUrl } from 'alakai-aloha-shared';
import { getRuntimeSiteContext } from '../lib/siteContext';
import { getPlaybookBasePath } from '../lib/playbookRoutes';

function normalizePath(value = '') {
  if (!value) return '';

  if (/^(?:[a-z]+:)?\/\//i.test(value)) {
    try {
      const url = new URL(value);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return value;
    }
  }

  return value;
}

export function isAbsoluteUrl(value) {
  return typeof value === 'string' && /^(?:[a-z]+:)?\/\//i.test(value);
}

function normalizePathname(value = '') {
  const normalized = normalizePath(value);
  if (!normalized) return '';

  try {
    const url = new URL(normalized, 'https://alakaialoha.com');
    const pathname = url.pathname || '';
    return pathname !== '/' ? pathname.replace(/\/+$/, '') : pathname;
  } catch {
    const pathname = normalized.split(/[?#]/, 1)[0] || '';
    return pathname !== '/' ? pathname.replace(/\/+$/, '') : pathname;
  }
}

export function toHref(value) {
  if (!value) return '';

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object') {
    return `${value.pathname || ''}${value.search || ''}${value.hash || ''}`;
  }

  return String(value);
}

export function resolveNavigationHref(value) {
  const href = toHref(value);

  if (!href || isAbsoluteUrl(href) || !isSeoOwnedPath(href)) {
    return href;
  }

  const siteContext = getRuntimeSiteContext();
  if (!siteContext.isApexHost) {
    return href;
  }

  return getIslandUrl(siteContext.island, href);
}

export function isSeoOwnedPath(value) {
  const href = normalizePath(toHref(value));
  const pathname = normalizePathname(href);
  const segments = pathname.split('/').filter(Boolean);
  const playbookBaseSegments = normalizePathname(getPlaybookBasePath(DEFAULT_ISLAND_KEY))
    .split('/')
    .filter(Boolean);
  const isDeepResearchPath =
    ['restaurants', 'activities', 'lodging', 'blog'].includes(segments[0] || '') &&
    segments.length === 3 &&
    segments[2] === 'deep-research';
  const isRestaurantDetail =
    segments[0] === 'restaurants' &&
    (segments.length === 2 || isDeepResearchPath) &&
    segments[1] !== 'list';
  const isActivityDetail =
    segments[0] === 'activities' &&
    (segments.length === 2 || isDeepResearchPath) &&
    segments[1] !== 'list';
  const isLodgingDetail =
    segments[0] === 'lodging' &&
    (segments.length === 2 || isDeepResearchPath) &&
    segments[1] !== 'list';
  const isPlaybookDetail =
    playbookBaseSegments.length > 0 &&
    playbookBaseSegments.every((segment, index) => segments[index] === segment) &&
    segments.length > playbookBaseSegments.length;
  const photoSegments = segments;
  const isPhotoDetail =
    photoSegments[0] === 'photo-planner' &&
    photoSegments.length >= 2 &&
    photoSegments[1] !== 'list';

  return (
    href === '/blog' ||
    href.startsWith('/blog/') ||
    href === '/guides' ||
    href.startsWith('/guides/') ||
    href === '/regions' ||
    href.startsWith('/regions/') ||
    href.startsWith('/areas/') ||
    isDeepResearchPath ||
    isPlaybookDetail ||
    isPhotoDetail ||
    isActivityDetail ||
    isRestaurantDetail ||
    isLodgingDetail
  );
}
